import { apiFetch } from "./client";
import type { FilterOption, Page } from "./admin";
import type { OfferStage } from "./offerStages";

/** Uppercase document-language code — one per portal locale. Stored uppercase
 * server-side (Offer.documentLanguage, offer_document_language_check). */
export type OfferDocLanguage =
  | "SR" | "EN" | "DE" | "ES" | "FR" | "HR" | "IT" | "RU" | "SL" | "TR";

export type OfferItem = {
  id: string;
  position: number;
  productId: string | null;
  sku: string | null;
  name: string;
  quantity: number;
  unit: string;
  unitPrice: number | null;
  discountPct: number;
  vatPct: number;
  lineTotal: number | null;
};

export type OfferTag = {
  id: string;
  name: string;
  color: string | null;
};

export type Offer = {
  id: string;
  offerNumber: string;
  ownerUserId: string;
  ownerName: string | null;
  companyId: string | null;
  customerName: string;
  customerAddress1: string | null;
  customerAddress2: string | null;
  customerCity: string | null;
  customerPostalCode: string | null;
  customerCountry: string | null;
  customerVatNumber: string | null;
  offerDate: string;
  validUntil: string | null;
  deliveryTerms: string | null;
  deliveryDate: string | null;
  paymentTerms: string | null;
  paymentTermsNote: string | null;
  /** Net-payment day count, set only when paymentTerms === "VALUTA". */
  paymentTermsDays: number | null;
  deliveryWeeks: number | null;
  vatEnabled: boolean;
  vatRate: number;
  /** Pipeline stage. Only
   * ever null defensively (offer.stage_id is NOT NULL). */
  stage: OfferStage | null;
  dueAt: string | null;
  expectedAmount: number | null;
  // Display preference (BE OfferView.of): the computed total over line items
  // wins once the offer has any; expectedAmount is the manual early-stage
  // estimate shown only while the deal has no items yet.
  displayAmount: number | null;
  tags: OfferTag[];
  currency: string;
  documentLanguage: OfferDocLanguage;
  items: OfferItem[];
  net: number;
  vatAmount: number;
  grandTotal: number;
  createdAt: string;
  updatedAt: string;
  /** True for an offer carried over from the ERP: the portal shows it, the ERP keeps it. */
  fromErp?: boolean;
  /** Transport cost on the quote: Bankom's own cost, so not in the total. */
  transportAmount?: number | null;
  previousOfferId?: string | null;
  /** Number of the offer this one was made from; on the offer's own screen. */
  previousOfferNumber?: string | null;
  /** Offers made from this one; on the offer's own screen. */
  laterOffers?: { id: string; offerNumber: string }[];
  /** ERP incoterm code and its place, carried into the order made from the offer. */
  incoterm?: string | null;
  incotermPlace?: string | null;
  /** The customer's contract the offer is made under; null for none. */
  contractId?: string | null;
  /** Who pays the transport: BANKOM (its own cost, unseen by the customer) or CUSTOMER (a line of the offer). */
  transportPaidBy?: "BANKOM" | "CUSTOMER";
};

export type ItemInput = {
  productId?: string | null;
  sku?: string | null;
  name: string;
  quantity: number;
  unit?: string;
  unitPrice?: number | null;
  discountPct?: number | null;
};

export type OfferUpsert = {
  companyId?: string | null;
  customerName: string;
  customerAddress1?: string | null;
  customerAddress2?: string | null;
  customerCity?: string | null;
  customerPostalCode?: string | null;
  customerCountry?: string | null;
  customerVatNumber?: string | null;
  /** Offer expiry (yyyy-mm-dd). Omitted/null on create → server defaults to offer date + 7 days. */
  validUntil?: string | null;
  deliveryTerms?: string | null;
  deliveryDate?: string | null;
  paymentTerms?: string | null;
  paymentTermsNote?: string | null;
  paymentTermsDays?: number | null;
  deliveryWeeks?: number | null;
  vatEnabled?: boolean;
  vatRate?: number;
  documentLanguage?: OfferDocLanguage;
  items: ItemInput[];
  /** Transport cost; on an edit, left out keeps it. */
  transportAmount?: number | null;
  /** ERP incoterm code and its place; on an edit, left out keeps them and an empty text clears them. */
  incoterm?: string | null;
  incotermPlace?: string | null;
  /** The contract the offer is made under; null for none. */
  contractId?: string | null;
  /** Who pays the transport; left out keeps it. */
  transportPaidBy?: "BANKOM" | "CUSTOMER";
};

export type CustomerSuggestion = {
  companyId: string;
  name: string;
  address1: string | null;
  address2: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  /** PIB: the company's own, or for a branch the parent's it shares. */
  vatNumber: string | null;
};

/** Dinars for one unit of a currency at the NBS middle rate of the ERP's list in force on {@code day}. */
export type DinarRate = { currency: string; day: string; dinars: number };

export type OfferNote = {
  id: string;
  authorUserId: string;
  authorName: string | null;
  body: string;
  createdAt: string;
};

/**
 * One merged offer-activity timeline entry — either an audit-log row
 * (type="AUDIT", action set, fromStageName/toStageName null) or a stage-move
 * row (type="STAGE_CHANGE", action null). Already sorted DESC by occurredAt
 * by the backend.
 */
export type OfferActivityEntry = {
  type: "AUDIT" | "STAGE_CHANGE";
  action: string | null;
  fromStageName: string | null;
  toStageName: string | null;
  actorUserId: string | null;
  actorName: string | null;
  occurredAt: string;
};

/** GET /api/offers/funnel row — one per active pipeline stage, in position order. */
export type OfferFunnelRow = {
  stageId: string;
  name: string;
  kind: OfferStage["kind"];
  count: number;
  totalAmount: number;
};

export const offersApi = {
  /**
   * Stranica ponuda. Filteri, brojanje i redosled rade u bazi: sa istorijom iz starog
   * sistema spisak ima sto hiljada redova, a nijedan ekran ih ne traži sve odjednom.
   */
  list: (params: {
    companyId?: string;
    stageId?: string;
    ownerId?: string;
    country?: string;
    dateFrom?: string;
    dateTo?: string;
    q?: string;
    page?: number;
    size?: number;
  } = {}) => {
    const usp = new URLSearchParams();
    Object.entries({ size: 50, ...params }).forEach(([k, v]) => {
      if (v !== undefined && v !== "") usp.set(k, String(v));
    });
    return apiFetch<Page<Offer>>(`/offers?${usp.toString()}`);
  },
  /** Brojači po fazi sa servera, nad istim skupom koji spisak prikazuje. */
  counts: (params: {
    companyId?: string;
    ownerId?: string;
    country?: string;
    dateFrom?: string;
    dateTo?: string;
    q?: string;
  } = {}) =>
    apiFetch<{
      counts: Record<string, number>;
      /** Zbir po fazi i po valuti, onako kako stoji na dokumentima. */
      amounts: Record<string, Record<string, number>>;
      /** Isti zbir preračunat u evre, po kursu na dan ponude. */
      eur: Record<string, number>;
      /** Per stage, offers in a currency without any exchange rate: not in the euro sum. */
      noRate?: Record<string, number>;
    }>(`/offers/counts${(() => {
      const usp = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v) usp.set(k, String(v)); });
      const x = usp.toString();
      return x ? `?${x}` : "";
    })()}`),
  /** Države koje se u spisku stvarno pojavljuju; `~` je grupa „bez države". */
  countries: () => apiFetch<FilterOption[]>("/offers/countries"),
  get: (id: string) => apiFetch<Offer>(`/offers/${id}`),
  create: (body: OfferUpsert) => apiFetch<Offer>("/offers", { method: "POST", body }),
  update: (id: string, body: OfferUpsert) => apiFetch<Offer>(`/offers/${id}`, { method: "PUT", body }),
  remove: (id: string) => apiFetch<void>(`/offers/${id}`, { method: "DELETE" }),
  /** Free movement between any two stages  — the BE enforces no FSM here. */
  transition: (id: string, stageId: string) =>
    apiFetch<Offer>(`/offers/${id}/transition`, { method: "POST", body: { stageId } }),
  /** Rejects when there is no rate today (no list, or the ERP cannot be read). */
  dinarRate: (currency: string) =>
    apiFetch<DinarRate>(`/offers/dinar-rate?currency=${encodeURIComponent(currency)}`),
  suggestCustomers: (q: string) =>
    apiFetch<CustomerSuggestion[]>(`/offers/customer-suggest?q=${encodeURIComponent(q)}`),
  listNotes: (id: string) => apiFetch<OfferNote[]>(`/offers/${id}/notes`),
  addNote: (id: string, body: string) =>
    apiFetch<OfferNote>(`/offers/${id}/notes`, { method: "POST", body: { body } }),
  /** Find-or-create by case-insensitive name, then attach to the offer. */
  attachTag: (id: string, name: string, color?: string | null) =>
    apiFetch<Offer>(`/offers/${id}/tags`, { method: "POST", body: { name, color } }),
  detachTag: (id: string, tagId: string) =>
    apiFetch<Offer>(`/offers/${id}/tags/${tagId}`, { method: "DELETE" }),
  /** dueAt === null clears the reminder. */
  setDue: (id: string, dueAt: string | null) =>
    apiFetch<Offer>(`/offers/${id}/due`, { method: "PATCH", body: { dueAt } }),
  funnel: () => apiFetch<OfferFunnelRow[]>("/offers/funnel"),
  activity: (id: string) => apiFetch<OfferActivityEntry[]>(`/offers/${id}/activity`),
};
